import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth, SIGNED_OUT_REASON_KEY } from '../../AuthContext';
import LogoBlog from "../../assets/logo.png";
import ReCAPTCHA from "react-google-recaptcha";

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailLink, setResetEmailLink] = useState('');
  const { login, userId, userRole, isAuthenticated, resetPassword } = useAuth();
  const [captchaValue, setCaptchaValue] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Say why they are here if they were signed out for inactivity, once.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(SIGNED_OUT_REASON_KEY) === 'idle') {
        sessionStorage.removeItem(SIGNED_OUT_REASON_KEY);
        toast.info('You were signed out after 30 minutes of inactivity. Please sign in again.', {
          autoClose: 8000,
        });
      }
    } catch {
      // storage blocked - nothing to show
    }
  }, []);

  // Handle navigation when user is already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Dismiss all toasts before navigation
      toast.dismiss();
      
      const intendedPath = location.state?.from?.pathname || 
                          (userRole === 'user' ? '/user/dashboard' : '/admin/dashboard');
      navigate(intendedPath, { replace: true });
    }
  }, [isAuthenticated, userRole, location, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
   if (!email || !password) {
    toast.error('Please fill in all fields');
    return;
  }

  if (!captchaValue) {
    toast.error('Please verify that you are not a robot');
    return;
  }

    setIsLoading(true);
    
    try {
      const result = await login({ 
        email, 
        password, 
        captcha: captchaValue 
      });
      
      if (result.success) {
        toast.success('Login successful!');
        // Navigation is handled by useEffect when isAuthenticated changes
      } else {
        console.error('error', result);
        toast.error(result.data?.data?.message || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      toast.error(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetEmail || !resetEmailLink) {
      toast.error('Please fill in all the fields.');
      return;
    }

    setIsLoading(true);

    try {
      const result = await resetPassword('proccess.php','staffs', {'email':resetEmail, 'emailLink': resetEmailLink});
      
      if (result.success) {
        toast.success(result.message);
        // Clear the modal and form
        setShowResetModal(false);
        setResetEmail('');
        setResetEmailLink('');
      } else {
        console.error('error', result);
        toast.error(result.message);
      }
    } catch (err) {
      console.error('Reset password error:', err);
      toast.error(err.message || 'Password reset failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='flex-column-center w-100' style={{ height: "auto" }}>
      <div className="login-container">
        <ToastContainer 
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false} // Change this to false
          rtl={false}
          pauseOnFocusLoss
          draggable={false} // Change this to false
          pauseOnHover
          limit={3} // Add a limit to prevent too many toasts
        />
        
        <div className="login-box bg-white">
          <div className='flex-raw-center w-100 mb-4 pb-4'>
            <div className='small-img'>
              <img className='image' src={LogoBlog} alt=''/>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="login-form">
            <input type="hidden" name="action" value="login" />
            
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={isLoading}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={isLoading}
                required
              />
            </div>
            
            <div className="form-group">
              <ReCAPTCHA
                sitekey="6Lez9o8sAAAAAO0869BEv2GU4dOrPge7E-VbV4IQ"
                onChange={(value) => setCaptchaValue(value)}
              />
            </div>
            
            <button 
              type="submit" 
              className="login-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span> Logging in...
                </>
              ) : 'Login'}
            </button>
          </form>
        </div>
      </div>

      {/* Password Reset Modal - Add this if you need it */}
      {showResetModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Reset Password</h3>
            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
              <div className="form-group">
                <label>Reset Link</label>
                <input
                  type="url"
                  value={resetEmailLink}
                  onChange={(e) => setResetEmailLink(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="submit" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button type="button" onClick={() => setShowResetModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;